/**
 * useCoreEvents — LIVE EVENT-DRIVEN PLATFORM sprint.
 *
 * The one React bridge to the CoreEventBus. A screen subscribes to ONLY the
 * events it needs; each delivery bumps a version counter, re-rendering the
 * component so it re-reads its data sources. No polling, no manual refresh.
 *
 * Usage:
 *   const busVersion = useCoreEvents(['LocationChanged', 'SystemStateChanged'], 'sidebar');
 *   useEffect(() => { recompute(); }, [busVersion]);
 *
 * The subscriberId names the screen in the Developer Center™ delivery log /
 * slow-subscriber panel — always pass a meaningful one.
 */

import { useEffect, useState } from 'react';
import { roamerCore } from '@/services/core';
import type { CoreEventName } from '@/services/core';

export function useCoreEvents(names: CoreEventName[], subscriberId: string): number {
  const [version, setVersion] = useState(0);
  // Names array is compared by content so callers can pass inline literals.
  const namesKey = names.join('|');
  useEffect(() => {
    const offs: Array<() => void> = [];
    try {
      for (const name of namesKey.split('|') as CoreEventName[]) {
        if (!name) continue;
        offs.push(roamerCore.bus.on(name, () => setVersion((v) => v + 1), subscriberId));
      }
    } catch {
      // Core unavailable (tests/SSR) — component simply stays static.
    }
    return () => offs.forEach((off) => off());
  }, [namesKey, subscriberId]);
  return version;
}
